import { DocumentData } from 'firebase/firestore';
import {
  ResourceFolder,
  ResourceFolderColor,
  ResourceLink,
  ResourceLinkKind,
  UserRole,
} from '../../types';
import { isValidWebUrl, toAbsoluteHttpUrl } from '../../utils/urlUtils';

export const RESOURCE_MAX_DEPTH = 5;

export const RESOURCE_FOLDER_COLORS: {
  id: ResourceFolderColor;
  iconClass: string;
  swatchClass: string;
}[] = [
  { id: 'brand', iconClass: 'text-brand-600', swatchClass: 'bg-brand-600' },
  { id: 'slate', iconClass: 'text-slate-600', swatchClass: 'bg-slate-500' },
  { id: 'sky', iconClass: 'text-sky-600', swatchClass: 'bg-sky-500' },
  { id: 'emerald', iconClass: 'text-emerald-600', swatchClass: 'bg-emerald-500' },
  { id: 'amber', iconClass: 'text-amber-600', swatchClass: 'bg-amber-500' },
  { id: 'orange', iconClass: 'text-orange-600', swatchClass: 'bg-orange-500' },
  { id: 'rose', iconClass: 'text-rose-600', swatchClass: 'bg-rose-500' },
  { id: 'violet', iconClass: 'text-violet-600', swatchClass: 'bg-violet-500' },
];

const FOLDER_COLOR_IDS = new Set(RESOURCE_FOLDER_COLORS.map((c) => c.id));

export function normalizeFolderColor(value: unknown): ResourceFolderColor {
  if (typeof value === 'string' && FOLDER_COLOR_IDS.has(value as ResourceFolderColor)) {
    return value as ResourceFolderColor;
  }
  return 'brand';
}

export function folderIconClass(color: ResourceFolderColor | undefined): string {
  const found = RESOURCE_FOLDER_COLORS.find((c) => c.id === (color ?? 'brand'));
  return found?.iconClass ?? 'text-brand-600';
}

export function coerceDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

export function mapFirestoreResourceFolder(id: string, data: DocumentData): ResourceFolder {
  return {
    id,
    name: String(data.name || ''),
    parentId: data.parentId == null || data.parentId === '' ? null : String(data.parentId),
    allowAdmins: Boolean(data.allowAdmins),
    allowCounselors: Boolean(data.allowCounselors),
    color: normalizeFolderColor(data.color),
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
    createdBy: String(data.createdBy || ''),
    createdAt: coerceDate(data.createdAt),
    updatedAt: coerceDate(data.updatedAt),
  };
}

export function mapFirestoreResourceLink(id: string, data: DocumentData): ResourceLink {
  const kind = data.kind;
  return {
    id,
    folderId: String(data.folderId || ''),
    title: String(data.title || ''),
    url: String(data.url || ''),
    kind:
      kind === 'doc' ||
      kind === 'sheet' ||
      kind === 'powerpoint' ||
      kind === 'pdf' ||
      kind === 'audio' ||
      kind === 'video' ||
      kind === 'other'
        ? kind
        : 'other',
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
    createdBy: String(data.createdBy || ''),
    createdAt: coerceDate(data.createdAt),
    updatedAt: coerceDate(data.updatedAt),
  };
}

export function canSeeResourceFolder(role: UserRole | undefined, folder: ResourceFolder): boolean {
  if (!role) return false;
  if (role === 'leader') return true;
  if (role === 'admin') return folder.allowAdmins || folder.allowCounselors;
  if (role === 'counselor') return folder.allowCounselors;
  return false;
}

/** Folder is visible only if it and every ancestor are visible to the role. */
export function isFolderVisibleInTree(
  role: UserRole | undefined,
  folder: ResourceFolder,
  byId: Map<string, ResourceFolder>
): boolean {
  let current: ResourceFolder | undefined = folder;
  const seen = new Set<string>();
  while (current) {
    if (seen.has(current.id)) return false;
    seen.add(current.id);
    if (!canSeeResourceFolder(role, current)) return false;
    if (!current.parentId) return true;
    current = byId.get(current.parentId);
    if (!current) return false;
  }
  return false;
}

export function folderDepth(folder: ResourceFolder, byId: Map<string, ResourceFolder>): number {
  let depth = 1;
  let parentId = folder.parentId;
  const seen = new Set<string>();
  while (parentId) {
    if (seen.has(parentId)) break;
    seen.add(parentId);
    depth += 1;
    const parent = byId.get(parentId);
    if (!parent) break;
    parentId = parent.parentId;
  }
  return depth;
}

export function inferResourceLinkKind(url: string): ResourceLinkKind {
  const lower = url.trim().toLowerCase();
  if (lower.includes('docs.google.com/spreadsheets') || lower.includes('sheets.google.com')) {
    return 'sheet';
  }
  if (
    lower.includes('docs.google.com/presentation') ||
    lower.includes('slides.google.com') ||
    lower.endsWith('.pptx') ||
    lower.endsWith('.ppt')
  ) {
    return 'powerpoint';
  }
  if (lower.includes('docs.google.com/document') || lower.includes('docs.google.com/doc')) {
    return 'doc';
  }
  if (
    lower.endsWith('.pdf') ||
    lower.includes('.pdf?') ||
    lower.includes('.pdf#') ||
    lower.includes('/pdf') ||
    lower.includes('application/pdf')
  ) {
    return 'pdf';
  }
  if (
    lower.includes('youtube.com') ||
    lower.includes('youtu.be') ||
    lower.includes('vimeo.com') ||
    lower.endsWith('.mp4') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.mov') ||
    lower.endsWith('.m4v')
  ) {
    return 'video';
  }
  if (
    lower.includes('soundcloud.com') ||
    lower.includes('spotify.com') ||
    lower.endsWith('.mp3') ||
    lower.endsWith('.wav') ||
    lower.endsWith('.m4a') ||
    lower.endsWith('.aac') ||
    lower.endsWith('.ogg')
  ) {
    return 'audio';
  }
  if (lower.includes('spreadsheet')) return 'sheet';
  if (lower.includes('presentation') || lower.includes('powerpoint')) return 'powerpoint';
  if (lower.includes('document')) return 'doc';
  if (lower.includes('pdf')) return 'pdf';
  if (lower.includes('video')) return 'video';
  if (lower.includes('audio') || lower.includes('podcast')) return 'audio';
  return 'other';
}

export function isValidResourceUrl(url: string): boolean {
  return isValidWebUrl(url);
}

export function openResourceUrl(url: string): void {
  window.open(toAbsoluteHttpUrl(url) ?? url, '_blank', 'noopener,noreferrer');
}

export interface ResourceTreeNode {
  folder: ResourceFolder;
  children: ResourceTreeNode[];
  links: ResourceLink[];
}

export function buildVisibleTree(
  role: UserRole | undefined,
  folders: ResourceFolder[],
  links: ResourceLink[]
): ResourceTreeNode[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const visible = folders.filter((f) => isFolderVisibleInTree(role, f, byId));
  const visibleIds = new Set(visible.map((f) => f.id));

  const linksByFolder = new Map<string, ResourceLink[]>();
  for (const link of links) {
    if (!visibleIds.has(link.folderId)) continue;
    const list = linksByFolder.get(link.folderId) ?? [];
    list.push(link);
    linksByFolder.set(link.folderId, list);
  }
  for (const list of Array.from(linksByFolder.values())) {
    list.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  }

  const childrenOf = new Map<string | null, ResourceFolder[]>();
  for (const folder of visible) {
    const key = folder.parentId;
    const list = childrenOf.get(key) ?? [];
    list.push(folder);
    childrenOf.set(key, list);
  }
  for (const list of Array.from(childrenOf.values())) {
    list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  const build = (parentId: string | null): ResourceTreeNode[] => {
    const kids = childrenOf.get(parentId) ?? [];
    return kids.map((folder) => ({
      folder,
      children: build(folder.id),
      links: linksByFolder.get(folder.id) ?? [],
    }));
  };

  return build(null);
}

/** Root folders only (parentId null). */
export function rootFoldersForParentSelect(folders: ResourceFolder[]): ResourceFolder[] {
  return folders
    .filter((f) => f.parentId == null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/** All descendant folder ids under rootId (not including rootId). */
export function collectDescendantFolderIds(
  rootId: string,
  folders: ResourceFolder[]
): string[] {
  const result: string[] = [];
  const walk = (id: string) => {
    for (const f of folders) {
      if (f.parentId === id) {
        result.push(f.id);
        walk(f.id);
      }
    }
  };
  walk(rootId);
  return result;
}

/** Whether a subfolder can be created under this folder (depth < max). */
export function canAddSubfolderUnder(
  parent: ResourceFolder,
  folders: ResourceFolder[]
): boolean {
  const byId = new Map(folders.map((f) => [f.id, f]));
  return folderDepth(parent, byId) < RESOURCE_MAX_DEPTH;
}

/** Height of subtree rooted at folderId (folder itself = 1). */
export function folderSubtreeHeight(folderId: string, folders: ResourceFolder[]): number {
  const children = folders.filter((f) => f.parentId === folderId);
  if (children.length === 0) return 1;
  return 1 + Math.max(...children.map((c) => folderSubtreeHeight(c.id, folders)));
}

/** Whether folderId can be placed under newParentId (null = become root). */
export function canMoveFolderUnder(
  folderId: string,
  newParentId: string | null,
  folders: ResourceFolder[]
): boolean {
  if (newParentId === folderId) return false;
  const descendants = collectDescendantFolderIds(folderId, folders);
  if (newParentId && descendants.includes(newParentId)) return false;

  const byId = new Map(folders.map((f) => [f.id, f]));
  if (newParentId) {
    const parent = byId.get(newParentId);
    if (!parent) return false;
  }

  const parentDepth = newParentId ? folderDepth(byId.get(newParentId)!, byId) : 0;
  const movedDepth = parentDepth + 1;
  const height = folderSubtreeHeight(folderId, folders);
  return movedDepth + height - 1 <= RESOURCE_MAX_DEPTH;
}

/** Parents a folder may be moved under (excludes self and descendants). */
export function listEligibleParentsForMove(
  folderId: string,
  folders: ResourceFolder[]
): ResourceFolder[] {
  return folders
    .filter((f) => canMoveFolderUnder(folderId, f.id, folders))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/** Path of the ancestor chain only, empty for root folders. */
export function folderParentPathLabel(
  folder: ResourceFolder,
  byId: Map<string, ResourceFolder>
): string {
  const parts: string[] = [];
  let parentId = folder.parentId;
  const seen = new Set<string>();
  while (parentId) {
    if (seen.has(parentId)) break;
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) break;
    parts.unshift(parent.name);
    parentId = parent.parentId;
  }
  return parts.join(' / ');
}

/** Ancestor folder ids, closest parent first. */
export function collectAncestorFolderIds(
  folderId: string,
  folders: ResourceFolder[]
): string[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const ids: string[] = [];
  const seen = new Set<string>();
  let current = byId.get(folderId);
  while (current?.parentId) {
    if (seen.has(current.parentId)) break;
    seen.add(current.parentId);
    ids.push(current.parentId);
    current = byId.get(current.parentId);
  }
  return ids;
}

export function folderPathLabel(
  folder: ResourceFolder,
  byId: Map<string, ResourceFolder>
): string {
  const parts: string[] = [folder.name];
  let parentId = folder.parentId;
  const seen = new Set<string>();
  while (parentId) {
    if (seen.has(parentId)) break;
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) break;
    parts.unshift(parent.name);
    parentId = parent.parentId;
  }
  return parts.join(' / ');
}

export const RESOURCE_KIND_ORDER: ResourceLinkKind[] = [
  'doc',
  'sheet',
  'powerpoint',
  'pdf',
  'audio',
  'video',
  'other',
];

export type ResourceKindFilter = ResourceLinkKind | 'all';

const COMBINING_MARKS = /[\u0300-\u036f]/g;

/** Lowercase and drop diacritics so „ședință” also matches „sedinta”. */
export function normalizeSearchText(value: string): string {
  return value.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
}

/** Query split into normalized terms; every term must match (AND). */
export function splitSearchTerms(query: string): string[] {
  return normalizeSearchText(query).trim().split(/\s+/).filter(Boolean);
}

export function resourceUrlHostname(url: string): string {
  try {
    const absolute = toAbsoluteHttpUrl(url);
    if (!absolute) return '';
    return new URL(absolute).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

export interface ResourceSearchResult {
  link: ResourceLink;
  folder: ResourceFolder;
  /** Folder path of the link, e.g. „Materiale / Formare”. */
  pathLabel: string;
}

/**
 * Flat link search over already-loaded resources. Runs entirely on the client
 * (0 Firestore reads) and never returns links from folders the role cannot see.
 * Matches link title, folder path and URL hostname.
 */
export function searchResourceLinks(
  role: UserRole | undefined,
  folders: ResourceFolder[],
  links: ResourceLink[],
  query: string
): ResourceSearchResult[] {
  const terms = splitSearchTerms(query);
  if (terms.length === 0) return [];

  const byId = new Map(folders.map((f) => [f.id, f]));
  const visible = new Map<string, ResourceFolder>();
  for (const folder of folders) {
    if (isFolderVisibleInTree(role, folder, byId)) visible.set(folder.id, folder);
  }

  const matches: { result: ResourceSearchResult; titleMatch: boolean }[] = [];
  for (const link of links) {
    const folder = visible.get(link.folderId);
    if (!folder) continue;

    const pathLabel = folderPathLabel(folder, byId);
    const title = normalizeSearchText(link.title);
    const path = normalizeSearchText(pathLabel);
    const host = normalizeSearchText(resourceUrlHostname(link.url));

    const matchesAll = terms.every(
      (term) => title.includes(term) || path.includes(term) || host.includes(term)
    );
    if (!matchesAll) continue;

    matches.push({
      result: { link, folder, pathLabel },
      titleMatch: terms.some((term) => title.includes(term)),
    });
  }

  // Title hits first, then grouped by folder path for a predictable reading order.
  matches.sort((a, b) => {
    if (a.titleMatch !== b.titleMatch) return a.titleMatch ? -1 : 1;
    const byPath = a.result.pathLabel.localeCompare(b.result.pathLabel, 'ro');
    if (byPath !== 0) return byPath;
    return a.result.link.title.localeCompare(b.result.link.title, 'ro');
  });

  return matches.map((m) => m.result);
}

export interface ResourceFolderSearchResult {
  folder: ResourceFolder;
  /** Ancestor path, empty for root folders. */
  parentPathLabel: string;
  /** Links directly inside the folder. */
  linkCount: number;
  /** Visible child folders directly inside the folder. */
  childFolderCount: number;
}

/**
 * Folders whose own name matches the query. Complements
 * `searchResourceLinks` so a matching folder is findable even when empty.
 */
export function searchResourceFolders(
  role: UserRole | undefined,
  folders: ResourceFolder[],
  links: ResourceLink[],
  query: string
): ResourceFolderSearchResult[] {
  const terms = splitSearchTerms(query);
  if (terms.length === 0) return [];

  const byId = new Map(folders.map((f) => [f.id, f]));
  const linkCounts = new Map<string, number>();
  for (const link of links) {
    linkCounts.set(link.folderId, (linkCounts.get(link.folderId) ?? 0) + 1);
  }

  const childCounts = new Map<string, number>();
  for (const folder of folders) {
    if (!folder.parentId) continue;
    if (!isFolderVisibleInTree(role, folder, byId)) continue;
    childCounts.set(folder.parentId, (childCounts.get(folder.parentId) ?? 0) + 1);
  }

  return folders
    .filter((folder) => {
      if (!isFolderVisibleInTree(role, folder, byId)) return false;
      const name = normalizeSearchText(folder.name);
      return terms.every((term) => name.includes(term));
    })
    .map((folder) => ({
      folder,
      parentPathLabel: folderParentPathLabel(folder, byId),
      linkCount: linkCounts.get(folder.id) ?? 0,
      childFolderCount: childCounts.get(folder.id) ?? 0,
    }))
    .sort((a, b) => {
      const byPath = a.parentPathLabel.localeCompare(b.parentPathLabel, 'ro');
      if (byPath !== 0) return byPath;
      return a.folder.name.localeCompare(b.folder.name, 'ro');
    });
}

export function countResourceResultsByKind(
  results: ResourceSearchResult[]
): Record<ResourceLinkKind, number> {
  const counts = {
    doc: 0,
    sheet: 0,
    powerpoint: 0,
    pdf: 0,
    audio: 0,
    video: 0,
    other: 0,
  } as Record<ResourceLinkKind, number>;
  for (const { link } of results) {
    counts[link.kind] += 1;
  }
  return counts;
}

/**
 * Ranges (in original string indices) of the query terms inside `text`, so a
 * match can be highlighted even though matching ignores diacritics and case.
 */
export function findResourceHighlightRanges(
  text: string,
  query: string
): [number, number][] {
  const terms = splitSearchTerms(query);
  if (!text || terms.length === 0) return [];

  // Normalizing per character keeps a normalized -> source index mapping.
  const sourceIndex: number[] = [];
  let normalized = '';
  for (let i = 0; i < text.length; i += 1) {
    const chunk = normalizeSearchText(text[i]);
    for (let j = 0; j < chunk.length; j += 1) {
      normalized += chunk[j];
      sourceIndex.push(i);
    }
  }

  const ranges: [number, number][] = [];
  for (const term of terms) {
    let from = 0;
    while (from <= normalized.length - term.length) {
      const found = normalized.indexOf(term, from);
      if (found < 0) break;
      const start = sourceIndex[found];
      const end = sourceIndex[found + term.length - 1] + 1;
      ranges.push([start, end]);
      from = found + term.length;
    }
  }
  if (ranges.length === 0) return [];

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [ranges[0]];
  for (const [start, end] of ranges.slice(1)) {
    const last = merged[merged.length - 1];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}
